/**
 * Calendar.jsx
 * A month at a glance: what is scheduled, what went out, what failed.
 *
 * Dates are rendered in London time from UTC values, so a post scheduled for
 * 19:30 shows at 19:30 whatever the browser's own timezone happens to be.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../services/api.js';
import { defaultScheduleValue, formatLocalInput, formatLondon, formatTime } from '../utils/time.js';
import { useNavigate } from 'react-router-dom';
import { Calendar as CalendarIcon, Warning, Check, Clock, ChevronRight } from '../components/Icons.jsx';

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export default function Calendar() {
  const navigate = useNavigate();
  const [month, setMonth] = useState(() => startOfMonth(new Date()));
  const [posts, setPosts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [moving, setMoving] = useState(false);
  const [when, setWhen] = useState(defaultScheduleValue);

  const range = useMemo(() => {
    const from = new Date(month);
    from.setDate(from.getDate() - 7);
    const to = new Date(month);
    to.setMonth(to.getMonth() + 1);
    to.setDate(to.getDate() + 7);
    return { from: from.toISOString(), to: to.toISOString() };
  }, [month]);

  const load = useCallback(async () => {
    try {
      const { posts: list } = await api.posts.calendar(range.from, range.to);
      setPosts(list);
      setError(null);
    } catch (e) {
      setError(e);
    }
  }, [range.from, range.to]);

  useEffect(() => {
    load();
  }, [load]);

  async function cancel(post) {
    if (!confirm('Cancel this scheduled post? It returns to drafts rather than being deleted.')) return;
    setBusy(true);
    try {
      await api.posts.cancel(post.id);
      setSelected(null);
      await load();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  /** Moving a post keeps its captions; cancel-and-recreate would lose them. */
  async function reschedule() {
    setBusy(true);
    setError(null);
    try {
      await api.posts.schedule(selected.id, when);
      setMoving(false);
      setSelected(null);
      await load();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  const cells = useMemo(() => buildGrid(month, posts), [month, posts]);
  const monthLabel = new Intl.DateTimeFormat('en-GB', {
    month: 'long',
    year: 'numeric',
    timeZone: 'Europe/London',
  }).format(month);

  return (
    <>
      <div className="page-head">
        <div>
          <div className="page-head__eyebrow">Calendar</div>
          <h1>{monthLabel}</h1>
          <p>Everything scheduled, published or failed. Times are London.</p>
        </div>
        <div className="page-head__actions">
          <button className="btn" onClick={() => setMonth(shiftMonth(month, -1))}>
            ← Previous
          </button>
          <button className="btn" onClick={() => setMonth(startOfMonth(new Date()))}>
            Today
          </button>
          <button className="btn" onClick={() => setMonth(shiftMonth(month, 1))}>
            Next →
          </button>
        </div>
      </div>

      {error && (
        <div className="notice notice--error">
          <Warning />
          <span>{error.message}</span>
        </div>
      )}

      <section className="card">
        <div className="cal">
          {WEEKDAYS.map((d) => (
            <div className="cal__head" key={d}>
              {d}
            </div>
          ))}

          {cells.map((cell) => (
            <div
              key={cell.key}
              className={`cal__day${cell.otherMonth ? ' is-muted' : ''}${cell.isToday ? ' is-today' : ''}`}
            >
              <div className="cal__date">{cell.date.getDate()}</div>

              {cell.posts.map((p) => (
                <button
                  key={p.id}
                  className={`chip chip--${statusClass(p.status)}`}
                  onClick={() => setSelected(p)}
                  title={p.preview ?? ''}
                >
                  <span className="chip__time">
                    {formatTime(p.scheduled_at_utc ?? p.published_at ?? p.created_at)}
                  </span>
                  {p.brand_name}
                </button>
              ))}
            </div>
          ))}
        </div>
      </section>

      {selected && (
        <div className="modal-backdrop" onClick={() => setSelected(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>{selected.brand_name}</h2>
            <p className="modal__sub">
              {formatLondon(selected.scheduled_at_utc ?? selected.published_at ?? selected.created_at)}
              {' · '}
              {(selected.platforms ?? '').split(',').join(' + ') || 'no platforms'}
            </p>

            <div className="confirm-body">
              {selected.media_id && (
                <img className="confirm-thumb" src={api.media.rawUrl(selected.media_id)} alt="" />
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="row">
                  <span className={`tile tile--sm tile--${tileClass(selected.status)}`}>
                    {selected.status === 'failed' ? (
                      <Warning size={17} />
                    ) : selected.status === 'published' ? (
                      <Check size={17} />
                    ) : (
                      <Clock size={17} />
                    )}
                  </span>
                  <div>
                    <div className="row__label">{selected.status}</div>
                    <div className="row__sub">{selected.preview?.slice(0, 140) ?? ''}</div>
                  </div>
                </div>
              </div>
            </div>

            {moving && (
              <div className="field" style={{ marginTop: 6 }}>
                <label htmlFor="move-to">Move to</label>
                <input
                  id="move-to"
                  type="datetime-local"
                  value={when}
                  min={defaultScheduleValue()}
                  onChange={(e) => setWhen(e.target.value)}
                />
                <p className="hint">
                  London time. Goes out {formatLocalInput(when)} instead, with the same captions
                  and image.
                </p>
              </div>
            )}

            <div className="btn-row modal__actions">
              {selected.status === 'scheduled' && !moving && (
                <button
                  className="btn btn--primary"
                  onClick={() => {
                    setWhen(defaultScheduleValue());
                    setMoving(true);
                  }}
                >
                  <Clock size={15} /> Reschedule
                </button>
              )}

              {moving && (
                <>
                  <button className="btn btn--primary" onClick={reschedule} disabled={busy}>
                    {busy ? 'Moving…' : 'Move it'}
                  </button>
                  <button className="btn" onClick={() => setMoving(false)} disabled={busy}>
                    Keep the current time
                  </button>
                </>
              )}

              {!moving && selected.status !== 'published' && (
                <button
                  className="btn"
                  onClick={() => navigate(`/create?post=${selected.id}`)}
                >
                  Edit
                </button>
              )}

              {selected.status === 'scheduled' && !moving && (
                <button className="btn btn--danger" onClick={() => cancel(selected)} disabled={busy}>
                  Cancel schedule
                </button>
              )}

              <button
                className="btn"
                onClick={() => {
                  setMoving(false);
                  setSelected(null);
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {posts.length === 0 && !error && (
        <div className="card">
          <div className="empty">
            <div className="empty__art">
              <CalendarIcon size={30} />
            </div>
            <h3>Nothing this month</h3>
            <p>Scheduled and published posts appear here.</p>
            <a className="btn btn--primary" href="/create">
              Create a post <ChevronRight size={16} />
            </a>
          </div>
        </div>
      )}
    </>
  );
}

// ============================================
// GRID BUILDING
// ============================================

const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const shiftMonth = (d, by) => new Date(d.getFullYear(), d.getMonth() + by, 1);

/** Six weeks starting on the Monday on or before the 1st. */
function buildGrid(month, posts) {
  const first = startOfMonth(month);
  const offset = (first.getDay() + 6) % 7; // Monday-first
  const start = new Date(first);
  start.setDate(first.getDate() - offset);

  const today = new Date();
  const byDay = new Map();
  for (const p of posts) {
    const when = new Date(p.scheduled_at_utc ?? p.published_at ?? p.created_at);
    const key = dayKey(when);
    if (!byDay.has(key)) byDay.set(key, []);
    byDay.get(key).push(p);
  }

  return Array.from({ length: 42 }, (_, i) => {
    const date = new Date(start);
    date.setDate(start.getDate() + i);
    return {
      key: date.toISOString(),
      date,
      otherMonth: date.getMonth() !== month.getMonth(),
      isToday: dayKey(date) === dayKey(today),
      posts: byDay.get(dayKey(date)) ?? [],
    };
  });
}

/** Day bucket in London time, so late-evening UTC posts land on the right day. */
function dayKey(date) {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/London',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

const statusClass = (s) =>
  s === 'published' ? 'ok' : s === 'failed' ? 'danger' : s === 'scheduled' ? 'accent' : 'muted';

const tileClass = (s) => (s === 'failed' ? 'danger' : s === 'published' ? 'ok' : 'warn');
